import { poll } from '@core/model/framework/notifications/polling';
import { BankConnection } from '@core/model/domain/bankConnection';
import { Bad } from '@core/model/framework/Result/BadResult';
import { Ok } from '@core/model/framework/Result/OkResult';
import { WaitOnWebauthNotificationGateway } from '@core/model/gateway/waitOnWebauthNotificationGateway';
import { GetBankConnections } from '@core/usecase/getBankConnections';
import {
  WaitOnWebauthNotificationInput,
  WaitOnWebauthNotificationOutput,
} from '@core/usecase/waitOnWebauthNotification';
import { SERVICE_ID } from '@di/serviceId';
import { inject, injectable } from 'inversify';
import { BehaviorSubject, Observable, TimeoutError, firstValueFrom, from } from 'rxjs';
import { assert } from 'ts-essentials';

@injectable()
export class WaitOnWebauthNotificationViaPolling implements WaitOnWebauthNotificationGateway {
  private existingBankConnections!: BankConnection[];
  private manualForceStopPolling$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    @inject(GetBankConnections)
    private readonly getBankConnections: GetBankConnections,
    @inject(SERVICE_ID.waitOnWebauthTimeoutInSeconds)
    private readonly timeoutInSeconds: number,
    @inject(SERVICE_ID.waitOnWebauthIntervalInSeconds)
    private readonly intervalInSeconds: number
  ) {}

  async awaitNewBankConnection(input: WaitOnWebauthNotificationInput): WaitOnWebauthNotificationOutput {
    const hasBankConnectionsBeenStored = (await this.storeExistingBankConnections(input)) === true;

    if (!hasBankConnectionsBeenStored) {
      return Bad('FAILED_TO_STORE_EXISTING_BANK_CONNECTIONS');
    } else {
      return this.getNewBankConnectionViaPolling(input);
    }
  }

  stopWaitingNewBankConnection(): void {
    this.manualForceStopPolling$.next(true);
    console.log('Polling - stopWaitingNewBankConnection');
  }

  private async storeExistingBankConnections(input: WaitOnWebauthNotificationInput): Promise<boolean> {
    const bankConnections = await this.getBankConnections.execute(input);

    if (bankConnections.success === true) {
      this.existingBankConnections = bankConnections.value;
      return true;
    } else {
      return false;
    }
  }

  private async getNewBankConnectionViaPolling(
    input: WaitOnWebauthNotificationInput
  ): Promise<WaitOnWebauthNotificationOutput> {
    try {
      const bankConnections = await firstValueFrom(
        poll<WaitOnWebauthNotificationInput, BankConnection[]>({
          intervalInSeconds: this.intervalInSeconds,
          timeoutInSeconds: this.timeoutInSeconds,
          apiCall: () =>
            this.getNewBankConnectionApiCall({
              userId: input.userId,
              userToken: input.userToken,
            }),
          shouldContinuePolling: (updatedBankConnections) =>
            this.shouldContinuePolling(this.existingBankConnections, updatedBankConnections),
          manualForceStop: this.manualForceStopPolling$,
        })
      );

      const newBankConnection = this.findNewBankConnection(this.existingBankConnections, bankConnections);
      assert(newBankConnection != null, 'should be defined to exit from polling');
      return Ok(newBankConnection);
    } catch (e) {
      if (e instanceof TimeoutError) {
        return Bad('WAITED_NEW_BANK_CONNECTION_FOR_TOO_LONG');
      } else if (e === 'STOP_POLLING_VIA_USER_ACTION') {
        this.resetUsecaseState();
        return Bad('STOP_POLLING_VIA_USER_ACTION');
      } else {
        throw new Error('getNewBankConnectionViaPolling throwed');
      }
    }
  }

  private resetUsecaseState() {
    this.manualForceStopPolling$.next(false);
  }

  private getNewBankConnectionApiCall = (
    input: WaitOnWebauthNotificationInput
  ): Observable<BankConnection[]> => {
    return new Observable((observer) => {
      from(
        this.getBankConnections.execute({
          userId: input.userId,
          userToken: input.userToken,
        })
      ).subscribe({
        next: (result) => {
          if (result.success === true) {
            observer.next(result.value);
          }
          // TODO : handle Ok / Bad
          observer.next([]);
        },
      });
    });
  };

  private shouldContinuePolling = (
    existingBankConnections: BankConnection[],
    updatedBankConnections: BankConnection[]
  ): boolean => {
    const newBankConnection = this.findNewBankConnection(existingBankConnections, updatedBankConnections);

    if (newBankConnection) {
      return false;
    }
    return true;
  };

  private findNewBankConnection(
    existingBankConnections: BankConnection[],
    updatedBankConnections: BankConnection[]
  ): BankConnection | undefined {
    const existingConnectionsId = existingBankConnections.map((c) => c.connectionId);

    if (existingBankConnections.length === updatedBankConnections.length) {
      return undefined;
    } else {
      const newBankConnection = updatedBankConnections.find((updatedConnection) => {
        if (!existingConnectionsId.includes(updatedConnection.connectionId)) {
          return true;
        } else {
          return false;
        }
      });

      return newBankConnection;
    }
  }
}
